import type { QueryClient } from "@tanstack/react-query";
import type { DashboardType } from "@playbookai/types";

/**
 * Query keys, tenant+user-scoped (shell convention: `["<domain>", …,
 * tenantId ?? "none", userId ?? "anon"]`) so a session/tenant switch can never
 * reuse another scope's cached configuration or widget data.
 */
export interface DashboardKeyScope {
  readonly tenantId: string | null | undefined;
  readonly userId: string | null | undefined;
}

function scopeSegments(scope: DashboardKeyScope): readonly string[] {
  return [scope.tenantId ?? "none", scope.userId ?? "anon"];
}

export const dashboardKeys = {
  all(scope: DashboardKeyScope): readonly unknown[] {
    return ["dashboard", ...scopeSegments(scope)];
  },
  /** The ensure/read query for one dashboard subject. */
  bySubject(
    scope: DashboardKeyScope,
    dashboardType: DashboardType,
    subjectId: string | null,
  ): readonly unknown[] {
    return [
      "dashboard",
      ...scopeSegments(scope),
      "subject",
      dashboardType,
      subjectId ?? "self",
    ];
  },
  /** Widget-data resolution for one page (ids sorted for key stability). */
  widgetData(
    scope: DashboardKeyScope,
    dashboardId: string,
    pageId: string,
    widgetInstanceIds: readonly string[],
  ): readonly unknown[] {
    return [
      "dashboard",
      ...scopeSegments(scope),
      "widget-data",
      dashboardId,
      pageId,
      [...widgetInstanceIds].sort().join(","),
    ];
  },
};

/**
 * Mark every resolved widget dataset stale, whatever page or subject it belongs
 * to, and refetch the ones on screen now.
 *
 * For a writer that changes what a DATASET returns rather than what the
 * dashboard is made of. Linking a KPI to a VSM is the case this exists for: the
 * write lands in `vsm.vsm_kpi_links`, and the KPI table on the monitoring
 * dashboard reads it through a provider — so nothing in the dashboard's own
 * configuration changes and no configuration query is stale. Only the resolved
 * DATA is, and until this ran the widget kept its 30-second cache and showed the
 * old rows until a full reload.
 *
 * Deliberately un-targeted. A caller like the KPI rail knows a link changed but
 * not which dashboard, page or widget instances display it — on Personal Home a
 * single link can affect a widget on somebody else's subject entirely. Matching
 * on the `widget-data` segment reaches those without the caller having to know
 * the key shape, which is this module's to own.
 *
 * The default `refetchType` is taken on purpose. An unmounted dashboard has no
 * viewer to go stale in front of, and being marked stale makes it refetch the
 * instant it remounts. `"all"` would only buy one resolve request per cached
 * page nobody is looking at, on every link and unlink. This module passes
 * `refetchType` explicitly when it wants anything other than the default.
 */
export function invalidateWidgetData(queryClient: QueryClient): Promise<void> {
  return queryClient.invalidateQueries({
    predicate: (query) =>
      query.queryKey[0] === "dashboard" &&
      // Positional, not `includes`. `scopeSegments` always contributes exactly
      // two elements, so the discriminator sits at index 3 of every key
      // `widgetData` builds. Scanning the whole key would let a tenant or user
      // id that happened to read "widget-data" drag a `bySubject` configuration
      // query in with it.
      query.queryKey[3] === "widget-data",
  });
}
